import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { RouterProvider, createRouter } from "@tanstack/react-router"
import { StrictMode, lazy, Suspense } from "react"
import ReactDOM from "react-dom/client"

const ReactQueryDevtools = lazy(() =>
  import("@tanstack/react-query-devtools").then((m) => ({
    default: m.ReactQueryDevtools,
  })),
)

// Import the generated route tree
import { routeTree } from "./routeTree.gen"
import type { RouterContext } from "./lib/routerContext"

import "./styles/index.css"

// Import i18n configuration
import { GoogleMapsProvider } from "./lib/GoogleMapsProvider"
import "./lib/i18n"
import LanguageProvider from "./lib/LanguageProvider"
import { PassphraseProvider } from "./lib/PassphraseProvider"
import { ThemeProvider } from "./lib/ThemeProvider"

if (import.meta.env.VITE_C2_VERSION === "True") {
  console.log("C2 Version")
  localStorage.removeItem("custom-theme-json")
}

// Create a new router instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
      retry: 2,
    },
  },
})

const router = createRouter({
  routeTree,
  context: {
    queryClient,
  } satisfies RouterContext,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
})

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById("app")
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <PassphraseProvider>
          <ThemeProvider defaultTheme="dark" storageKey="vite-ui-theme">
            <LanguageProvider>
              <GoogleMapsProvider>
                <RouterProvider router={router} />
                <Suspense fallback={null}>
                  <ReactQueryDevtools
                    initialIsOpen={false}
                    buttonPosition="bottom-left"
                  />
                </Suspense>
              </GoogleMapsProvider>
            </LanguageProvider>
          </ThemeProvider>
        </PassphraseProvider>
      </QueryClientProvider>
    </StrictMode>,
  )
}
